import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-semibold transition-all duration-200 ease-out disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-navy-500/40 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-[0_1px_2px_rgba(15,40,71,0.08),0_2px_8px_rgba(15,40,71,0.08)] hover:bg-primary/90 hover:shadow-[0_2px_4px_rgba(15,40,71,0.12),0_4px_14px_rgba(15,40,71,0.12)] active:shadow-[0_1px_2px_rgba(15,40,71,0.08)]',
        destructive:
          'bg-destructive text-white shadow-[0_1px_2px_rgba(220,38,38,0.12),0_2px_8px_rgba(220,38,38,0.12)] hover:bg-destructive/90 hover:shadow-[0_2px_4px_rgba(220,38,38,0.18),0_4px_14px_rgba(220,38,38,0.18)] focus-visible:ring-destructive/30 dark:bg-destructive/60',
        outline:
          'border border-slate-300 bg-white text-slate-700 shadow-[0_1px_2px_rgba(15,40,71,0.04)] hover:border-navy-400 hover:text-navy-700 hover:bg-navy-50/40 hover:shadow-[0_2px_4px_rgba(15,40,71,0.08)] dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary:
          'bg-secondary text-secondary-foreground shadow-[0_1px_2px_rgba(15,40,71,0.04)] hover:bg-secondary/80 hover:shadow-[0_2px_4px_rgba(15,40,71,0.08)]',
        ghost:
          'hover:bg-slate-100 hover:text-navy-700 dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-14 px-5 py-3 has-[>svg]:px-4',
        sm: 'h-11 rounded-md gap-1.5 px-4 has-[>svg]:px-3',
        lg: 'h-16 rounded-md px-8 has-[>svg]:px-6',
        icon: 'size-14',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : 'button'
  // Coarse-pointer (tablet) touch-target floor: primary tap surfaces opt in
  // via data-tap="primary" so the @media (pointer: coarse) rule in
  // app/globals.css promotes them to 56px min-height without inflating
  // ghost / link utility buttons. Authors can override by passing
  // data-tap explicitly through props.
  const isPrimaryTap = variant !== 'ghost' && variant !== 'link'

  return (
    <Comp
      data-slot="button"
      data-tap={isPrimaryTap ? 'primary' : undefined}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }

export { Button, buttonVariants }
